import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';
import { onUpgrade402 } from '../utils/onUpgrade402';

/**
 * Cafe theme schedules (Cafe Scheduling redesign, Option A — feed model). Backs the
 * visual weekly Theme Timeline. A schedule attaches a whole THEME (campaign:
 * background + music + corners) to a device or group for a daily window.
 *
 *   GET    /campaign-schedules[?device_id=&group_id=]   → { schedules }
 *   POST   /campaign-schedules    (campaign_id, schedulable_type, schedulable_id, window…)
 *   PUT    /campaign-schedules/{id}
 *   DELETE /campaign-schedules/{id}
 *
 * Times are sent as plain venue WALL-CLOCK 'HH:MM:SS' — NO UTC conversion (that
 * browser-timezone pre-shift was the legacy bug). The backend stores them verbatim
 * and resolves in the company timezone.
 *
 * Backend wraps in a Laravel resource ({ data: { status, ... } }); unwrap with
 * `res?.data ?? res`.
 */
const LIST_KEY = 'campaignSchedules';

export const useGetCampaignSchedules = (params = {}) =>
  useQuery([LIST_KEY, params], async () => {
    const res = (await axiosInstance.get('/campaign-schedules', { params })).data;
    return (res?.data ?? res)?.schedules ?? [];
  }, { staleTime: 10_000 });

export const useCreateCampaignSchedule = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/campaign-schedules', payload), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('success', 'Schedule', 'Theme scheduled.'); },
    onError: (err) => {
      if (onUpgrade402(err, showToast)) return;
      showToast('error', 'Schedule', err.response?.data?.message);
    },
  });
};

export const useUpdateCampaignSchedule = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, ...payload }) => axiosInstance.put(`/campaign-schedules/${id}`, payload), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('success', 'Schedule', 'Schedule updated.'); },
    onError: (err) => {
      if (onUpgrade402(err, showToast)) return;
      showToast('error', 'Schedule', err.response?.data?.message);
    },
  });
};

export const useDeleteCampaignSchedule = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/campaign-schedules/${id}`), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('info', 'Schedule', 'Schedule removed.'); },
    onError: (err) => showToast('error', 'Schedule', err.response?.data?.message),
  });
};

/**
 * "Stop Schedule" (device card, stops whatever's running right now) and "Skip
 * This Occurrence" (calendar, sits out a specific date) are the same backend
 * op, and it TOGGLES: sit out the next occurrence, or — if that occurrence is
 * already sat out — restore it ("Undo Skip"). Pass `date` ('Y-m-d') to target
 * a specific occurrence; omit it to default to whichever date the schedule
 * would next actually play (matches each row's `next_occurrence_date` from
 * useGetCampaignSchedules, so the calendar's indicator and this call always
 * agree on which date is "it").
 *   POST /campaign-schedules/{id}/skip  { date? }  → { skipped, date, ... }
 */
export const useSkipCampaignSchedule = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, date }) => axiosInstance.post(`/campaign-schedules/${id}/skip`, date ? { date } : {}), {
    onSuccess: (res) => {
      qc.invalidateQueries(LIST_KEY);
      const body = res?.data?.data ?? res?.data ?? {};
      showToast('success', 'Schedule', body.msg || (body.skipped ? 'Occurrence skipped.' : 'Occurrence restored.'));
    },
    onError: (err) => showToast('error', 'Schedule', err.response?.data?.message),
  });
};

/**
 * Bulk-apply one device's/group's whole theme plan to other devices/groups (REPLACE).
 *   POST /campaign-schedules/copy  { from:{type,id}, to:[{type,id}], mode:'replace' }
 */
export const useCopyCampaignSchedule = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/campaign-schedules/copy', payload), {
    onSuccess: () => { qc.invalidateQueries(LIST_KEY); showToast('success', 'Schedule', 'Schedule copied.'); },
    onError: (err) => {
      if (onUpgrade402(err, showToast)) return;
      showToast('error', 'Schedule', err.response?.data?.message);
    },
  });
};

/**
 * Flat list of the company's campaigns (THEMES) for the timeline's theme picker.
 * Hits /campaign/all with a big page so the dropdown has everything at once.
 */
export const useGetThemeOptions = () =>
  useQuery('cafeThemeOptions', async () => {
    const { data } = await axiosInstance.get('/campaign/all', { params: { page: 1, per_page: 200 } });
    return (data?.data ?? []).map((c) => ({ id: c.id, name: c.name }));
  }, { staleTime: 60_000 });
